import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sparkle, UploadSimple, Trash, ImageSquare, CircleNotch } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function CoverBanner({ room, onRoom }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      if (data.room) onRoom(data.room);
      if (okMsg) toast.success(okMsg);
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const generate = () => {
    if (!title.trim()) return toast.error("Введите название");
    run(() => api.post("/watch/cover/generate", { title }), "Афиша сгенерирована");
  };

  const uploadFile = (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    run(() => api.post("/watch/cover/upload", fd), "Обложка обновлена");
  };

  return (
    <div className="space-y-2">
      {(room?.has_cover || room?.cover_title) && (
        <div className="glass-card overflow-hidden relative h-32 sm:h-40" data-testid="watch-cover-banner">
          {room.has_cover && (
            <img src={`${BASE}/api/watch/cover/image?v=${room.cover_v}`} alt="Обложка"
              className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute bottom-3 left-4 right-4">
            <p className="text-[10px] uppercase tracking-widest text-white/60">Сейчас смотрим</p>
            <h2 className="font-heading font-bold text-white text-lg sm:text-xl truncate" data-testid="watch-cover-title">
              {room.cover_title || room.title || "Совместный просмотр"}
            </h2>
          </div>
        </div>
      )}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setTitle(room?.cover_title || ""); }}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" data-testid="watch-cover-btn">
            <ImageSquare size={15} className="mr-1.5" />Обложка комнаты
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Обложка комнаты</DialogTitle>
            <DialogDescription>Афиша и название того, что вы смотрите вместе</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="cover-title-input"
              placeholder="Название: «Друзья, 5 сезон», «Аватар»…" />
            <div className="flex gap-2 flex-wrap">
              <Button onClick={generate} disabled={busy} data-testid="cover-generate-btn">
                {busy ? <CircleNotch size={15} className="mr-1.5 animate-spin" /> : <Sparkle size={15} className="mr-1.5" weight="fill" />}
                Сгенерировать AI-афишу
              </Button>
              <label>
                <input type="file" accept="image/*" className="hidden" data-testid="cover-upload-input"
                  onChange={(e) => uploadFile(e.target.files?.[0])} />
                <Button variant="outline" disabled={busy} asChild>
                  <span className="cursor-pointer"><UploadSimple size={15} className="mr-1.5" />Загрузить свою</span>
                </Button>
              </label>
              {(room?.has_cover || room?.cover_title) && (
                <Button variant="ghost" disabled={busy} data-testid="cover-delete-btn"
                  onClick={() => run(() => api.delete("/watch/cover"), "Обложка убрана")}>
                  <Trash size={15} className="mr-1.5" />Убрать
                </Button>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">AI нарисует киноафишу по названию (Gemini). Или загрузите свой постер (до 10 МБ).</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
